import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import usuarioService from '../../services/usuarioService';
import useAuthStore from '../../store/authStore';

export default function AsignarPostulantes() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [postulantesDisponibles, setPostulantesDisponibles] = useState([]);
  const [misPostulantes, setMisPostulantes] = useState([]);
  const [searchDisponibles, setSearchDisponibles] = useState('');
  const [searchMisPostulantes, setSearchMisPostulantes] = useState('');
  const [selectedDisponibles, setSelectedDisponibles] = useState([]);
  const [selectedMisPostulantes, setSelectedMisPostulantes] = useState([]);

  useEffect(() => {
    loadPostulantes();
  }, []);

  const loadPostulantes = async () => {
    try {
      setLoading(true);
      const data = await usuarioService.getAllUsuarios();
      
      // Filtrar solo postulantes
      const postulantes = data.filter(u => u.rol === 'POSTULANTE');
      
      // Separar entre disponibles (sin supervisor) y mis postulantes
      const disponibles = postulantes.filter(p => !p.supervisorId);
      const asignados = postulantes.filter(p => p.supervisorId === user.id);
      
      setPostulantesDisponibles(disponibles);
      setMisPostulantes(asignados);
    } catch (error) {
      toast.error('Error al cargar postulantes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignar = async () => {
    if (selectedDisponibles.length === 0) {
      toast.error('Seleccione al menos un postulante');
      return;
    }

    try {
      // Asignar supervisorId a cada postulante seleccionado
      const promises = selectedDisponibles.map(id => {
        const postulante = postulantesDisponibles.find(p => p.id === id);
        return usuarioService.updateUsuario(id, {
          ...postulante,
          supervisorId: user.id
        });
      });

      await Promise.all(promises);
      toast.success(`${selectedDisponibles.length} postulante(s) asignado(s)`);
      setSelectedDisponibles([]);
      loadPostulantes();
    } catch (error) {
      toast.error('Error al Movimiento');
      console.error(error);
    }
  };

  const handleDesasignar = async () => {
    if (selectedMisPostulantes.length === 0) {
      toast.error('Seleccione al menos un postulante');
      return;
    }

    try {
      // Remover supervisorId de cada postulante seleccionado
      const promises = selectedMisPostulantes.map(id => {
        const postulante = misPostulantes.find(p => p.id === id);
        return usuarioService.updateUsuario(id, {
          ...postulante,
          supervisorId: null
        });
      });

      await Promise.all(promises);
      toast.success(`${selectedMisPostulantes.length} postulante(s) desasignado(s)`);
      setSelectedMisPostulantes([]);
      loadPostulantes();
    } catch (error) {
      toast.error('Error al desMovimiento');
      console.error(error);
    }
  };

  const toggleSelectDisponible = (id) => {
    setSelectedDisponibles(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectMisPostulantes = (id) => {
    setSelectedMisPostulantes(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllDisponibles = () => {
    if (selectedDisponibles.length === filteredDisponibles.length) {
      setSelectedDisponibles([]);
    } else {
      setSelectedDisponibles(filteredDisponibles.map(p => p.id));
    }
  };

  const selectAllMisPostulantes = () => {
    if (selectedMisPostulantes.length === filteredMisPostulantes.length) {
      setSelectedMisPostulantes([]);
    } else {
      setSelectedMisPostulantes(filteredMisPostulantes.map(p => p.id));
    }
  };

  const filteredDisponibles = postulantesDisponibles.filter(p =>
    p.name?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.lastname?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.dni?.includes(searchDisponibles) ||
    p.email?.toLowerCase().includes(searchDisponibles.toLowerCase())
  );

  const filteredMisPostulantes = misPostulantes.filter(p =>
    p.name?.toLowerCase().includes(searchMisPostulantes.toLowerCase()) ||
    p.lastname?.toLowerCase().includes(searchMisPostulantes.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchMisPostulantes.toLowerCase()) ||
    p.dni?.includes(searchMisPostulantes) ||
    p.email?.toLowerCase().includes(searchMisPostulantes.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Movimiento</h1>
          <p className="text-neutral-600 mt-1">Gestione los postulantes asignados a usted</p>
        </div>
        <Button variant="outline" onClick={loadPostulantes} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Postulantes Disponibles */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-4 border-b border-neutral-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-neutral-800">
                Postulantes Disponibles
              </h2>
              <Badge variant="secondary">{filteredDisponibles.length}</Badge>
            </div>
            <Input
              placeholder="Buscar..."
              value={searchDisponibles}
              onChange={(e) => setSearchDisponibles(e.target.value)}
              icon={Search}
            />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedDisponibles.length === filteredDisponibles.length && filteredDisponibles.length > 0}
                  onChange={selectAllDisponibles}
                  className="rounded border-neutral-300"
                />
                Seleccionar todos
              </label>
            </div>
          </div>
          <div className="overflow-y-auto max-h-96">
            {filteredDisponibles.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                No hay postulantes disponibles
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {filteredDisponibles.map((postulante) => (
                  <label
                    key={postulante.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      selectedDisponibles.includes(postulante.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDisponibles.includes(postulante.id)}
                      onChange={() => toggleSelectDisponible(postulante.id)}
                      className="rounded border-neutral-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-800 truncate">
                        {postulante.name} {postulante.lastname}
                      </p>
                      <p className="text-sm text-neutral-600 truncate">
                        {postulante.email}
                      </p>
                      <p className="text-xs text-neutral-500">
                        DNI: {postulante.dni}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Botones de Asignación */}
        <div className="lg:col-span-1 flex flex-col items-center justify-center gap-4">
          <Button
            onClick={handleAsignar}
            disabled={selectedDisponibles.length === 0}
            className="gap-2 w-full max-w-xs"
          >
            Asignar a mi lista
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleDesasignar}
            disabled={selectedMisPostulantes.length === 0}
            variant="outline"
            className="gap-2 w-full max-w-xs"
          >
            <ChevronLeft className="w-4 h-4" />
            Remover de mi lista
          </Button>
          <div className="text-center text-sm text-neutral-600 mt-4">
            <p>Disponibles: {selectedDisponibles.length} seleccionados</p>
            <p>Mis postulantes: {selectedMisPostulantes.length} seleccionados</p>
          </div>
        </div>

        {/* Mis Postulantes */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-4 border-b border-neutral-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-neutral-800">
                Mis Postulantes
              </h2>
              <Badge variant="success">{filteredMisPostulantes.length}</Badge>
            </div>
            <Input
              placeholder="Buscar..."
              value={searchMisPostulantes}
              onChange={(e) => setSearchMisPostulantes(e.target.value)}
              icon={Search}
            />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedMisPostulantes.length === filteredMisPostulantes.length && filteredMisPostulantes.length > 0}
                  onChange={selectAllMisPostulantes}
                  className="rounded border-neutral-300"
                />
                Seleccionar todos
              </label>
            </div>
          </div>
          <div className="overflow-y-auto max-h-96">
            {filteredMisPostulantes.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                No tiene postulantes asignados
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {filteredMisPostulantes.map((postulante) => (
                  <label
                    key={postulante.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      selectedMisPostulantes.includes(postulante.id) ? 'bg-green-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMisPostulantes.includes(postulante.id)}
                      onChange={() => toggleSelectMisPostulantes(postulante.id)}
                      className="rounded border-neutral-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-800 truncate">
                        {postulante.name} {postulante.lastname}
                      </p>
                      <p className="text-sm text-neutral-600 truncate">
                        {postulante.email}
                      </p>
                      <p className="text-xs text-neutral-500">
                        DNI: {postulante.dni}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
